import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { formatCurrency } from '../lib/format';
import { sendLead } from '../lib/webhook';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiDollarSign, FiTrendingUp, FiMail, FiCalendar, FiLoader, FiCheck, FiX } = FiIcons;

const EstimatePanel = ({ estimate, formData }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error'

  const handleSendEmail = async () => {
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      await sendLead({
        ...formData,
        estimate,
        action: 'estimate_request',
        timestamp: new Date().toISOString()
      });
      setSubmitStatus('success');
    } catch (error) {
      console.error('Error sending lead:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConsultation = async () => {
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      await sendLead({
        ...formData,
        estimate,
        action: 'consultation_request',
        timestamp: new Date().toISOString()
      });
      setSubmitStatus('success');
    } catch (error) {
      console.error('Error sending consultation request:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="bg-white rounded-2xl shadow-lg overflow-hidden"
    >
      {/* Header */}
      <div 
        className="px-6 py-4 text-white"
        style={{ backgroundColor: '#ff7f50' }}
      >
        <h3 className="text-xl font-bold flex items-center">
          <SafeIcon icon={FiDollarSign} className="h-6 w-6 mr-2" />
          Stima del potenziale
        </h3>
        <p className="text-orange-100 text-sm">
          Basata sui dati inseriti e sulle condizioni di mercato
        </p>
      </div>

      <div className="p-6 space-y-6">
        {/* Key Metrics */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-gray-50 rounded-xl p-4 text-center">
            <div className="text-2xl font-bold" style={{ color: '#0b294b' }}>
              {formatCurrency(estimate.adrBase)}
            </div>
            <div className="text-sm text-gray-600">ADR stimato/notte</div>
          </div>
          
          <div className="bg-gray-50 rounded-xl p-4 text-center">
            <div className="text-2xl font-bold" style={{ color: '#0b294b' }}>
              {Math.round(estimate.occ * 100)}%
            </div>
            <div className="text-sm text-gray-600">Occupazione stimata</div>
          </div>
        </div>

        {/* Monthly Earnings */}
        <div>
          <h4 className="font-semibold mb-3 flex items-center" style={{ color: '#0b294b' }}>
            <SafeIcon icon={FiCalendar} className="h-5 w-5 mr-2" />
            Entrate mensili stimate
          </h4>
          <div className="bg-gradient-to-r from-orange-50 to-orange-100 rounded-xl p-4">
            <div className="text-center">
              <div className="text-3xl font-bold mb-2" style={{ color: '#0b294b' }}>
                {formatCurrency(estimate.monthlyMin)} - {formatCurrency(estimate.monthlyMax)}
              </div>
              <div className="text-sm text-gray-600 mb-3">
                Lordo (prima delle commissioni OTA ~15%)
              </div>
              <div className="text-lg font-semibold" style={{ color: '#ff7f50' }}>
                Netto indicativo: {formatCurrency(estimate.monthlyMin * 0.85)} - {formatCurrency(estimate.monthlyMax * 0.85)}
              </div>
            </div>
          </div>
        </div>

        {/* Annual Earnings */}
        <div>
          <h4 className="font-semibold mb-3 flex items-center" style={{ color: '#0b294b' }}>
            <SafeIcon icon={FiTrendingUp} className="h-5 w-5 mr-2" />
            Entrate annuali stimate
          </h4>
          <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl p-4">
            <div className="text-center">
              <div className="text-3xl font-bold mb-2" style={{ color: '#0b294b' }}>
                {formatCurrency(estimate.annualMin)} - {formatCurrency(estimate.annualMax)}
              </div>
              <div className="text-sm text-gray-600 mb-3">
                Lordo (prima delle commissioni OTA ~15%)
              </div>
              <div className="text-lg font-semibold" style={{ color: '#ff7f50' }}>
                Netto indicativo: {formatCurrency(estimate.annualMin * 0.85)} - {formatCurrency(estimate.annualMax * 0.85)}
              </div>
            </div>
          </div>
        </div>

        {/* Seasonal Breakdown (if applicable) */}
        {formData.periodType === 'seasonal' && estimate.breakdown?.seasonal && (
          <div>
            <h4 className="font-semibold mb-3" style={{ color: '#0b294b' }}>
              Distribuzione stagionale
            </h4>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Alta stagione (4 mesi)</span>
                <div className="flex items-center space-x-2">
                  <div 
                    className="h-2 rounded-full"
                    style={{ 
                      width: '60px',
                      backgroundColor: '#ff7f50'
                    }}
                  />
                  <span className="text-sm font-medium">75%</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Media stagione (4 mesi)</span>
                <div className="flex items-center space-x-2">
                  <div 
                    className="h-2 rounded-full"
                    style={{ 
                      width: '40px',
                      backgroundColor: '#ffa366'
                    }}
                  />
                  <span className="text-sm font-medium">55%</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Bassa stagione (4 mesi)</span>
                <div className="flex items-center space-x-2">
                  <div 
                    className="h-2 rounded-full"
                    style={{ 
                      width: '20px',
                      backgroundColor: '#ffcc99'
                    }}
                  />
                  <span className="text-sm font-medium">35%</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Success/Error Messages */}
        {submitStatus === 'success' && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center space-x-3"
          >
            <SafeIcon icon={FiCheck} className="h-5 w-5 text-green-600" />
            <div>
              <div className="font-medium text-green-800">Richiesta inviata con successo!</div>
              <div className="text-sm text-green-600">Ti contatteremo entro 24 ore.</div>
            </div>
          </motion.div>
        )}

        {submitStatus === 'error' && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center space-x-3"
          >
            <SafeIcon icon={FiX} className="h-5 w-5 text-red-600" />
            <div>
              <div className="font-medium text-red-800">Errore nell'invio</div>
              <div className="text-sm text-red-600">Riprova o contattaci direttamente.</div>
            </div>
          </motion.div>
        )}

        {/* Action Buttons */}
        {submitStatus !== 'success' && (
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={handleSendEmail}
              disabled={isSubmitting}
              className="flex-1 flex items-center justify-center space-x-2 px-6 py-3 text-white rounded-xl font-semibold transition-all duration-200 hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: '#ff7f50' }}
            >
              {isSubmitting ? (
                <SafeIcon icon={FiLoader} className="h-5 w-5 animate-spin" />
              ) : (
                <SafeIcon icon={FiMail} className="h-5 w-5" />
              )}
              <span>Ricevi la stima via email</span>
            </button>

            <button
              onClick={handleConsultation}
              disabled={isSubmitting}
              className="flex-1 flex items-center justify-center space-x-2 px-6 py-3 border-2 rounded-xl font-semibold transition-all duration-200 hover:bg-opacity-10 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ 
                color: '#ff7f50',
                borderColor: '#ff7f50'
              }}
            >
              {isSubmitting ? (
                <SafeIcon icon={FiLoader} className="h-5 w-5 animate-spin" />
              ) : (
                <SafeIcon icon={FiCalendar} className="h-5 w-5" />
              )}
              <span>Prenota consulenza gratuita</span>
            </button>
          </div>
        )}

        {/* Breakdown Details */}
        {estimate.breakdown && (
          <details className="text-sm">
            <summary className="cursor-pointer font-medium text-gray-700 hover:text-gray-900">
              Dettagli calcolo
            </summary>
            <div className="mt-3 p-4 bg-gray-50 rounded-lg space-y-2">
              <div className="flex justify-between">
                <span>ADR base ({formData.propertyType}):</span>
                <span>{formatCurrency(estimate.breakdown.baseAdr)}</span>
              </div>
              {estimate.breakdown.adjustments?.map((adj, index) => (
                <div key={index} className="flex justify-between text-sm">
                  <span>{adj.reason}:</span>
                  <span className={adj.amount >= 0 ? 'text-green-600' : 'text-red-600'}>
                    {adj.amount >= 0 ? '+' : ''}{formatCurrency(adj.amount)}
                  </span>
                </div>
              ))}
              <div className="border-t pt-2 flex justify-between font-medium">
                <span>ADR finale:</span>
                <span>{formatCurrency(estimate.adrBase)}</span>
              </div>
              <div className="flex justify-between">
                <span>Occupazione media:</span>
                <span>{Math.round(estimate.occ * 100)}%</span>
              </div>
            </div>
          </details>
        )}
      </div>
    </motion.div>
  );
};

export default EstimatePanel;